import { useState, type ReactElement } from "react";
import { Popover } from "@base-ui/react/popover";
import { Link } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api";
import { Button } from "../ui/button";
import { AgentCopyButton } from "../../../../../packages/design/src/AgentCopyButton";
import { DEFAULT_RUN_INSTRUCTIONS, runAgainPrompt, runWithNotePrompt } from "../../../../../packages/contracts/src/local-prompt";
import type { MethodFacts } from "../../../../../packages/contracts/src/dashboard";
import { duration } from "./format";

export type RunTarget = {
  methodId: string; methodName: string; versionId: string | null; versionNumber?: number | null;
  facts: MethodFacts | null; typicalMs: number | null; lastResultRunId?: string | null;
};
export type RunAgain = { runId: string; createdAt: string; keys: string[]; more: number; device: boolean;
  /** The Method runs in Method Cloud: Run now starts it there with the same inputs. */
  cloud?: boolean };

/**
 * Run hands the run to the person's coding agent: it finds the inputs (files, folders, today's date) as the Method's
 * run prompt says. The popover shows what the agent gets, takes one optional note, and copies the prompt. A run of a
 * Method in Method Cloud can also start again at once with the same inputs (Run now).
 */
export function RunPopover({ target, again, trigger }: { target: RunTarget; again?: RunAgain; trigger: ReactElement }) {
  const [note, setNote] = useState("");
  const client = useQueryClient();
  const runNow = useMutation({
    mutationFn: () => api.runNow(target.methodId, again!.runId),
    onSuccess: () => client.invalidateQueries({ queryKey: ["runs"] }),
  });
  const facts = target.facts;
  const device = again?.device ?? facts?.run_data === "device";
  const server = window.location.origin;
  const prompt = !target.versionId ? "" : again
    ? runAgainPrompt(target.methodId, null, server, { id: again.runId, device }, note, target.methodName, target.versionNumber)
    : runWithNotePrompt(target.methodId, null, server, facts?.run_prompt ?? undefined, note, target.methodName, target.versionNumber);
  const where = device ? "Your agent runs it on your computer · run content stays there" : facts?.needs_environment.length
    ? `Your agent runs it on your computer · it needs ${facts.needs_environment.join(", ")}` : "Your agent runs it on your computer";
  const when = new Date(again?.createdAt ?? 0).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  return <Popover.Root onOpenChange={open => { if (!open) { setNote(""); runNow.reset(); } }}>
    <Popover.Trigger render={trigger} />
    <Popover.Portal>
      <Popover.Positioner side="bottom" align="end" sideOffset={6} collisionPadding={12} className="run-pop-positioner">
        <Popover.Popup className="run-pop" aria-label={again ? `Run ${target.methodName} again` : `Run ${target.methodName}`}>
          <div className="pp-top"><b>{again ? `Run ${target.methodName} again` : `Run ${target.methodName}`}</b><Popover.Close className="pp-x" aria-label="Close">×</Popover.Close></div>
          {again ? <p className="pp-line">{device
            ? <>Same inputs as the run of {when}. They are on your computer, in the folder of that run. Method reads them there.</>
            : <>Same inputs as the run of {when}{again.keys.length ? `: ${again.keys.join(", ")}${again.more ? ` +${again.more} more` : ""}` : ""}. Your agent passes <code>--inputs-from</code> with this run.</>}</p>
            : <p className="pp-line">{where}{target.typicalMs !== null ? ` · about ${duration(target.typicalMs)}` : ""}{facts?.result.length ? `. You get ${facts.result.join(", ")}.` : "."}</p>}
          {!again && <div className="pp-prompt">
            <span className="pp-lbl">{facts?.run_prompt ? "Your agent gets the Method's run prompt:" : "This Method has no run prompt. Your agent gets the default:"}</span>
            “{clip(facts?.run_prompt ?? DEFAULT_RUN_INSTRUCTIONS, 220)}”
            <span className="pp-cmd">+ the link to this version and the <code>method run</code> command</span>
          </div>}
          <label className="pp-note"><span>{again ? "What to change" : "Note for your agent"} <i>optional</i></span>
            <input value={note} onChange={event => setNote(event.target.value)} maxLength={500}
              placeholder={again ? "For example: reply to the other customer instead" : facts?.label_input ? `For example: the ${facts.label_input.replaceAll("_", " ")} to use` : "For example: for yesterday"} />
          </label>
          {again?.cloud && !device && <div className="pp-act">
            {runNow.isSuccess ? <p className="pp-line">Started in Method Cloud. It is at the top of Runs.</p>
              : <Button onClick={() => runNow.mutate()} disabled={runNow.isPending}>{runNow.isPending ? "Starting…" : "Run now in Method Cloud"}</Button>}
            {runNow.error && <p className="pp-error">{runNow.error.message}</p>}
          </div>}
          <div className="pp-act">
            <AgentCopyButton text={prompt} label="Copy prompt for your agent" disabled={!prompt} />
            {!again && target.lastResultRunId && <Link className="pp-link" to="/runs/$runId" params={{ runId: target.lastResultRunId }}>Open the last result</Link>}
          </div>
        </Popover.Popup>
      </Popover.Positioner>
    </Popover.Portal>
  </Popover.Root>;
}
const clip = (text: string, max: number) => text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
